import { memo, useCallback, useMemo, useState } from 'react';

import merge from 'lodash.merge';

import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import LoadingIndicator from '@/components/ui/LoadingIndicator';
import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import { TextEllipsis } from '@/components/ui/utils/textEllipsis/textEllipsis';
import type { TextView } from '@/components/ui/widget/views/text/text.view.type';
import type { WidgetData } from '@/components/ui/widget/widget.type';
import { getFileIconStr } from '@/utils';
import { downloadFile } from '@/utils/downloadFile';
import { getByPath } from '@/utils/widgets';
import { UploadFile } from './editors/uploadFile/uploadFile.editor.fileCompact';
import type { FileCompactView } from './fileCompact.view.type';

type Props = {
  view: FileCompactView;
  data: WidgetData;
  update: any; // TODO: type this
};

export type FileCompactData = {
  mimetype?: string;
  name?: string;
};

// compact file view
const FileCompactImpl = memo(({ view, data, update }: Props) => {
  const [loading, setLoading] = useState(false);
  const [downloadResponse, setDownloadResponse] = useState(null);

  const { path, defaultIcon, editor } = view;
  const { patch, onChange, setIsPatched, editorOverrides = {} } = update || {};

  const dataWithPatchApplied = useMemo(
    () => merge({}, data, patch),
    [data, patch]
  );

  const fileData = getByPath<FileCompactData>(dataWithPatchApplied, path);

  const downloadFileFn = useCallback(
    () => downloadFile(fileData, setLoading, loading, setDownloadResponse),
    [fileData]
  );

  const disabledPath = editor?.disabledPath;
  const disabledFromData = !!getByPath(data, disabledPath);

  const disabled = editorOverrides.disabled || !onChange || disabledFromData;

  if ((!fileData || typeof fileData !== 'object') && !editor) return null;

  switch (editor?.type) {
    case 'uploadFile':
      return (
        <UploadFile
          data={data}
          defaultIcon={defaultIcon}
          disabled={disabled}
          fileData={fileData}
          onChange={onChange}
          path={path}
          setIsPatched={setIsPatched}
        />
      );
    default: {
      const iconStr = getFileIconStr(
        fileData?.mimetype,
        fileData?.name,
        defaultIcon || 'DocumentDownload'
      );

      return (
        <LoadingIndicator
          showSuccess={downloadResponse !== null}
          spinning={loading}
        >
          <div
            className="flex h-full grow cursor-pointer flex-row flex-nowrap items-center justify-start overflow-hidden"
            data-cy="download-link"
            onClick={downloadFileFn}
          >
            <DynamicIcon icon={iconStr} size={24} />
            <TextEllipsis
              className="ml-1 grow overflow-hidden underline"
              middleEllipsis
              text={fileData?.name}
            />
          </div>
        </LoadingIndicator>
      );
    }
  }
});

// sort defaults to text on file name
const getSortConfig = (view: FileCompactView): TextView => ({
  type: 'text',
  path: `${view.path}.name`
});

// filtering defaults to text search on file name
const getFilterConfig = (view: FileCompactView): TextView => ({
  type: 'text',
  path: `${view.path}.name`
});

// width defaults to 'medium'
const getDefaultWidth = (): ColumnWidth => 'medium' as const;

// return file name
const getStringifyFunction = (view: FileCompactView) => {
  const { path } = view;
  return (data: WidgetData) => {
    const fileData = getByPath(data, path);
    return fileData?.name || '';
  };
};

export const fileCompactViewProvider = {
  component: FileCompactImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
